import { type DocumentNode } from 'graphql';

import { getAtlAttributionData } from '@canvas/atl-attribution';

import { type ExtendedFetcher } from './atlassian-client-types';
import { type DelegatingClient, type GraphQLOperations } from './delegating-client-types';
import { GraphQLClient, type MutationResult, type QueryResult } from './graphql';
import { type FetchPolicy, type RequestContext } from './graphql/types';

const jiraTypes = [
  'JiraIssueFieldConfiguration',
  'JiraProjectField',
  'JiraIssueTypeField',
  'JiraSingleLineTextField',
  'JiraRichTextField',
  'JiraPriorityField',
  'JiraSprintField',
  'JiraLabelsField',
  'JiraSingleSelectUserPickerField',
  'JiraMultipleSelectUserPickerField',
  'JiraEpicLinkField',
  'JiraDatePickerField',
  'JiraDateTimePickerField',
  'JiraNumberField',
  'JiraComponentsField',
  'JiraSingleSelectField',
  'JiraAttachmentsField',
  'JiraIssueLinkField',
  'JiraCheckboxesField',
  'JiraCascadingSelectField',
  'JiraConnectMultipleSelectField',
  'JiraConnectNumberField',
  'JiraConnectReadOnlyField',
  'JiraConnectRichTextField',
  'JiraConnectSingleSelectField',
  'JiraConnectTextField',
  'JiraDevSummaryField',
  'JiraFlagField',
  'JiraStatusCategoryField',
  'JiraIssueRestrictionField',
  'JiraBooleanField',
  'JiraColorField',
  'JiraMultipleGroupPickerField',
  'JiraMultipleSelectField',
  'JiraMultipleVersionPickerField',
  'JiraOriginalTimeEstimateField',
  'JiraParentIssueField',
  'JiraPeopleField',
  'JiraRadioSelectField',
  'JiraRequestTypeField',
  'JiraResolutionField',
  'JiraSecurityLevelField',
  'JiraSingleGroupPickerField',
  'JiraSingleVersionPickerField',
  'JiraStatusField',
  'JiraSubtasksField',
  'JiraTeamField',
  'JiraTeamViewField',
  'JiraTimeTrackingField',
  'JiraUrlField',
  'JiraVotesField',
  'JiraWatchesField',
  'JiraWorkCategoryField',
  'JiraWorkLogField',
  'JsmRequestTypeField', // Deprecated in favour of JiraServiceManagementRequestTypeField
  'JsmMultipleSelectUserPickerField',
  'JsmOrganizationField',
  'JsmPeopleField',
  'JiraServiceManagementRequestTypeField',
  'JiraAtlassianTeamField',
];

/**
 * Possible types for Jira GraphQL schema.
 * This is used for type discrimination in cached responses.
 */
export const jiraPossibleTypes = {
  JiraIssueField: jiraTypes,
  JiraIssueFieldConfiguration: jiraTypes,
};

/**
 * Creates a wrapper around GraphQLClient that injects atl-attribution headers.
 */
function createClientWrapper(client: GraphQLClient): GraphQLOperations {
  const injectAttribution = (context?: RequestContext): RequestContext => {
    const attributionData = getAtlAttributionData();
    if (!attributionData) {
      return context ?? {};
    }

    return {
      ...context,
      headers: {
        ...context?.headers,
        'atl-attribution': JSON.stringify(attributionData),
      },
    };
  };

  return {
    query<TData, TVariables extends object>(options: {
      query: string | DocumentNode;
      variables?: TVariables;
      context?: RequestContext;
      fetchPolicy?: FetchPolicy;
    }): Promise<QueryResult<TData>> {
      return client.query<TData, TVariables>({
        query: options.query,
        variables: options.variables,
        context: injectAttribution(options.context),
        fetchPolicy: options.fetchPolicy,
      });
    },

    mutate<TData, TVariables extends object>(options: {
      mutation: string | DocumentNode;
      variables?: TVariables;
      context?: RequestContext;
    }): Promise<MutationResult<TData>> {
      return client.mutate<TData, TVariables>({
        mutation: options.mutation,
        variables: options.variables,
        context: injectAttribution(options.context),
      });
    },
  };
}

/**
 * GraphQL isn't designed to be used with multiple endpoints.
 * To utilise caching here we store multiple clients by baseurl
 * getting them when a query is requested.
 *
 * Consider memory when using this class. It's intended use is to only
 * have a few GraphQL clients in memory at one time.
 */
export class DelegatingGraphQLClient implements DelegatingClient {
  constructor(private readonly fetcher?: ExtendedFetcher) {}

  private readonly graphqlClients: Map<string, GraphQLOperations> = new Map();

  getOrCreate(baseUrl: string, endpoint: string): GraphQLOperations {
    let clientWrapper = this.graphqlClients.get(baseUrl);

    if (!clientWrapper) {
      const fullEndpoint = `${baseUrl}${endpoint}`;

      const client = new GraphQLClient({
        endpoint: fullEndpoint,
        fetcher: this.fetcher,
        retry: this.fetcher
          ? undefined
          : {
              maxAttempts: 3,
              initialDelay: 200,
              maxDelay: 1000,
              jitter: true,
            },
      });

      clientWrapper = createClientWrapper(client);
      this.graphqlClients.set(baseUrl, clientWrapper);
    }

    return clientWrapper;
  }
}
